import React, { useCallback, useEffect } from "react";
import TreeView from "../tree/TreeView";
import LazyFieldList from "./LazyFieldList";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { ContextMenuTrigger } from "react-contextmenu";
import { fieldContainsSearchString, fieldMatchesSearchString } from "../../reducers/fields";
import highlightForSearch from "../../helpers/highlightForSearch";
import HtmlTooltip from "../material-components/Misc/HtmlTooltip";
import makeTooltipForField from "./makeTooltipForField";
import FieldFolderRename from "../tree-general/FolderRename";
import FieldFolderDragDrop from "./FieldFolderDragDrop";
import { FieldVisibilityTypes } from "../../helpers/constants";
import { getTreeSearch, getModalTreeSearch } from "../../reducers/search";
import { IAppState } from "../../types/stores/index";
import { IField } from "../../types/stores/fieldTypes";
import { IFieldsStore } from "../../types/stores/fields";
import {
    requestLazyChildFields,
    toggleExpandFolder,
    requestLazyChildFieldsAdmin,
} from "../../actions/fieldTreeActions";
import { sortChildIds } from "./FieldList";

type Props = {
    fieldKey: string | number;
    treeViewOnUpdate?: () => void;
    showAllDuringSearch?: boolean;
    TreeType: number;
    editable: boolean;
    showHiddenFields: boolean;
    ctrlShift: boolean;
    isModal: boolean;
    tabType: number;
    showTabs: boolean;
};

/**
 * Renders a collapsible folder in the field tree with lazy loading support.
 * Children are only loaded when the folder is expanded for the first time.
 *
 * Key Features:
 * - Lazy loading of child fields on first expansion
 * - Search functionality with text and label filtering
 * - Supports rename operations in edit mode
 * - Drag-and-drop support in view mode
 * - Context menu for admin operations
 * - Auto-expand during active search
 * - Handles mode transitions (view/edit) gracefully
 */
const LazyFieldListFolder: React.FC<Props> = (props: Props) => {
    const {
        fieldKey,
        treeViewOnUpdate,
        showAllDuringSearch,
        TreeType,
        editable,
        showHiddenFields,
        ctrlShift,
        isModal,
        tabType,
        showTabs,
    } = props;

    const dispatch = useDispatch();

    // Create memoized selectors
    const getFolderContainsSearchString = makeGetFolderContainsSearchString();
    const getRenamingAndHasNoChildren = makeGetRenamingAndHasNoChildren();
    const getHasChildRenaming = makeGetHasRenamingChild();

    const isexpanded = useSelector((state: IAppState) => !!state.vars?.expanded?.[fieldKey]);

    // Track which folders have had their children loaded
    const loadedFolders = useSelector((state: IAppState) => state.vars?.loadedFolders ?? {});
    const isLoaded = !!loadedFolders[fieldKey];

    const isLoading = useSelector((state: IAppState) => {
        const loadingFolders = state.vars?.loadingFolders ?? {};
        return loadingFolders[fieldKey] === true;
    });

    // Get field label and umbrella label search results from Redux
    const fieldLabelSearchResults = useSelector((state: IAppState) => state.vars.fieldLabelSearchResults);
    const umbrellaLabelSearchResults = useSelector((state: IAppState) => state.vars.umbrellaLabelSearchResults);

    // Fields based on TreeType
    const fields = useSelector((state: IAppState) => {
        if (TreeType === FieldVisibilityTypes.EXPORT) return state.layoutfields;
        if (TreeType === FieldVisibilityTypes.REPORT) return state.reportFields;
        return state.fields;
    });

    const field: IField = useSelector((state: IAppState) => {
        // Priority: text search > field label search > umbrella label search > normal
        const hasTextSearch = state.vars.booleanSearchResultsReceived === true;
        const hasFieldLabelSearch = fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0;
        const hasUmbrellaLabelSearch = umbrellaLabelSearchResults && Object.keys(umbrellaLabelSearchResults).length > 0;

        if (hasTextSearch && state.vars.searchResults[fieldKey]) {
            return state.vars.searchResults[fieldKey];
        }
        if (hasFieldLabelSearch && fieldLabelSearchResults[fieldKey]) {
            return fieldLabelSearchResults[fieldKey];
        }
        if (hasUmbrellaLabelSearch && umbrellaLabelSearchResults[fieldKey]) {
            return umbrellaLabelSearchResults[fieldKey];
        }
        return fields?.byFolder?.[fieldKey];
    });

    // Use searchResultsByParent when available, otherwise use regular byParent
    const fieldsByParent = useSelector((state: IAppState) => {
        const hasTextSearch = state.vars.booleanSearchResultsReceived === true;
        const hasFieldLabelSearch = fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0;
        const hasUmbrellaLabelSearch = umbrellaLabelSearchResults && Object.keys(umbrellaLabelSearchResults).length > 0;

        if (hasTextSearch && state.vars.searchResultsByParent) {
            return state.vars.searchResultsByParent;
        }
        if (hasFieldLabelSearch && state.vars.fieldLabelSearchResultsByParent) {
            return state.vars.fieldLabelSearchResultsByParent;
        }
        if (hasUmbrellaLabelSearch && state.vars.umbrellaLabelSearchResultsByParent) {
            return state.vars.umbrellaLabelSearchResultsByParent;
        }

        return fields?.byParent;
    });

    const treeSearch: string = useSelector((state: IAppState) =>
        isModal ? getModalTreeSearch(state) : getTreeSearch(state)
    );
    const hasServerSearchResults = useSelector((state: IAppState) => state.vars.booleanSearchResultsReceived === true);
    const folderMatchesSearchString =
        treeSearch && field && !hasServerSearchResults ? fieldMatchesSearchString(field, treeSearch) : false;

    // Field Labels Search
    const selectedFieldLabels = useSelector(
        (state: IAppState) =>
            state.fieldLabels.labels
                .filter(x => state.search.selectedFieldLabels.includes(x.FieldLabelId))
                .map(x => x.FieldLabelName) || []
    );

    // Umbrella Label Search
    const selectedFieldLabels3 = useSelector(
        (state: IAppState) =>
            state.fieldLabels3.labels
                .filter(x => x.FieldLabelId == state.search.selectedFieldLabels3)
                .map(x => x.FieldLabelName) || []
    );

    const folderContainsSearchString = useSelector((state: IAppState) => getFolderContainsSearchString(state, props));

    const renamingAndHasNoChildren = useSelector((state: IAppState) => getRenamingAndHasNoChildren(state, props));
    const hasRenamingChild = useSelector((state: IAppState) => getHasChildRenaming(state, props));
    const isFolderRenaming = useSelector((state: IAppState) => state.vars.renamingFieldFolderId == field?.workid);
    const pathname = useSelector(
        (state: IAppState) => state.router && state.router.location && state.router.location.pathname
    );

    const hasChildren =
        field && field.workid && fieldsByParent
            ? fieldsByParent[field.workid] && fieldsByParent[field.workid].length > 0
            : false;

    const prevEditable = useSelector((state: IAppState) => state.vars.prevEditable);

    useEffect(() => {
        // When editable mode changes, collapse folders that aren't loaded in the new cache
        if (prevEditable !== undefined && prevEditable !== editable) {
            // If switching modes and this folder isn't loaded in the new cache, collapse it
            if (isexpanded && !isLoaded) {
                dispatch(toggleExpandFolder(fieldKey));
            }
        }
    }, [editable, prevEditable, isexpanded, isLoaded, fieldKey, dispatch]);

    // Handles folder expand/collapse with lazy loading.
    const handleToggle = useCallback(() => {
        const nextExpand = !isexpanded;

        dispatch(toggleExpandFolder(fieldKey));

        // Lazy-load only when expanding for the first time
        if ((nextExpand || !isLoaded) && !isLoading) {
            const loadChildren = editable ? requestLazyChildFieldsAdmin : requestLazyChildFields;

            dispatch(
                loadChildren(
                    fieldKey,
                    () => {
                        if (treeViewOnUpdate) treeViewOnUpdate();
                    },
                    editable
                )
            );
        }
    }, [isexpanded, isLoaded, isLoading, fieldKey, dispatch, treeViewOnUpdate, editable]);

    // Check if any search is active
    const hasFieldLabelSearch = fieldLabelSearchResults && Object.keys(fieldLabelSearchResults).length > 0;
    const hasUmbrellaLabelSearch = umbrellaLabelSearchResults && Object.keys(umbrellaLabelSearchResults).length > 0;
    const isSearchActive = !!(treeSearch || hasFieldLabelSearch || hasUmbrellaLabelSearch);

    // Automatically expands folders during search to show matching children.
    useEffect(() => {
        if (!isSearchActive || isLoading) {
            return;
        }

        // load for text and umbrellaid searches
        const shouldLazyLoad = treeSearch || hasUmbrellaLabelSearch;

        // Trigger lazy load if not loaded - don't expand yet
        if (!isLoaded) {
            if (shouldLazyLoad) {
                const loadChildren = editable ? requestLazyChildFieldsAdmin : requestLazyChildFields;
                dispatch(
                    loadChildren(
                        fieldKey,
                        () => {
                            // After data is loaded, then expand the folder
                            if (!isexpanded) {
                                dispatch(toggleExpandFolder(fieldKey));
                            }
                            if (treeViewOnUpdate) treeViewOnUpdate();
                        },
                        editable
                    )
                );
            }
        } else if (!isexpanded) {
            // Data already loaded, expand immediately
            dispatch(toggleExpandFolder(fieldKey));
        }
    }, [
        isSearchActive,
        hasFieldLabelSearch,
        hasUmbrellaLabelSearch,
        isexpanded,
        isLoaded,
        isLoading,
        fieldKey,
        dispatch,
        treeViewOnUpdate,
        editable,
    ]);

    // If field not found, skip rendering
    if (!field) return null;

    //Renders the folder label with appropriate UI based on state:
    // - Edit mode + renaming: Shows rename input control
    // - Edit mode: Shows label with loading spinner and context menu
    // - View mode: Shows label with drag-and-drop support
    // - Search: Highlights matching text
    const renderFolderLabel = (): React.ReactElement => {
        let { text } = field;
        text = highlightForSearch(text, treeSearch);

        if (editable) {
            // check for folder being renamed and display rename control
            if (isFolderRenaming) {
                return (
                    <div style={{ display: "inline-block", width: "calc(100% - 23px)", paddingRight: "0px" }}>
                        <FieldFolderRename
                            folderName={field.text}
                            folderId={field.workid}
                            key={field.workid}
                            isFieldFolder={true}
                        />
                    </div>
                );
            } else {
                let inlineBlock;

                if (pathname) {
                    if (pathname.includes("/admin/fields/edit")) {
                        inlineBlock = { margin: "-26px 30px 0px" };
                    } else {
                        inlineBlock = { display: "inline-block", width: "calc(100% - 23px)", paddingRight: "0px" };
                    }
                } else {
                    inlineBlock = { display: "inline-block", width: "calc(100% - 23px)", paddingRight: "0px" };
                }

                return (
                    <div style={inlineBlock}>
                        {isLoading && <i className="fa fa-spinner fa-spin" style={{ marginRight: 5 }} />}
                        {text}
                        <div className="pull-right tree-links"></div>
                    </div>
                );
            }
        } else if (isLoading) {
            return (
                <span>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 5 }} />
                    {text}
                </span>
            );
        }

        return <span>{text}</span>;
    };

    // Tooltip
    const tt = makeTooltipForField(field, treeSearch);
    let headerMap: ((x: JSX.Element) => JSX.Element) | null = null;

    if (editable) {
        // Edit mode: Enable drag-and-drop folder moves
        headerMap = x => (
            <FieldFolderDragDrop field={field} toolTip={tt}>
                {x}
            </FieldFolderDragDrop>
        );
    } else {
        // View mode (Flow): keep context menu only, no folder drag-and-drop
        headerMap = x => (
            <div>
                <ContextMenuTrigger
                    id="edit-context"
                    collect={props => props}
                    key={field.workid}
                    holdToDisplay={-1}
                    // @ts-ignore
                    item={field}
                    //@ts-ignore
                    hasChildren={hasChildren}
                >
                    <HtmlTooltip placement="right" title={tt} maxwidth={500} enterDelay={250}>
                        <div>{x}</div>
                    </HtmlTooltip>
                </ContextMenuTrigger>
            </div>
        );
    }

    // Collapse & Visibility Logic
    let collapsed: boolean | undefined = undefined;
    let defaultCollapsed = true;
    let showAllInSearch = showAllDuringSearch ?? false;
    let itemClassName = "";

    // Handle rename state
    if (isFolderRenaming) {
        itemClassName += " renaming";
        headerMap = null; // Don't allow drag and drop or context menu during rename
        collapsed = renamingAndHasNoChildren; // Force folder open if renaming something with children
    }

    if (hasRenamingChild) {
        collapsed = false;
    }

    // Handle search filtering (text + field labels + umbrella labels)
    if (treeSearch || selectedFieldLabels.length > 0 || selectedFieldLabels3.length > 0) {
        // If folder name matches text search, show all its children
        if (treeSearch && folderMatchesSearchString) {
            showAllInSearch = true;
        }

        const notInTreeSearch = treeSearch && !folderMatchesSearchString && !folderContainsSearchString;

        if (notInTreeSearch) {
            // Don't render folder if not in any search results
            return null;
        }

        // Folder is in search results - auto-expand
        defaultCollapsed = false;
        if (isLoaded || isexpanded) {
            collapsed = false; // Force open during search
        }
    }

    return (
        <TreeView
            nodeLabel={renderFolderLabel()}
            key={fieldKey}
            defaultCollapsed={defaultCollapsed}
            collapsed={collapsed !== undefined ? collapsed : !isexpanded}
            onToggle={() => handleToggle()}
            onUpdate={treeViewOnUpdate}
            headerMap={headerMap}
            itemClassName={itemClassName}
        >
            <div>
                {/* Render children only when expanded or during search, and data is loaded */}
                {(isexpanded || isSearchActive || hasFieldLabelSearch || hasUmbrellaLabelSearch) && isLoaded ? (
                    <LazyFieldList
                        key={fieldKey}
                        fieldKey={fieldKey}
                        showAllDuringSearch={showAllInSearch}
                        treeViewOnUpdate={treeViewOnUpdate!}
                        TreeType={TreeType}
                        showHiddenFields={showHiddenFields}
                        editable={editable}
                        isPIIOnly={false}
                        ctrlShift={ctrlShift}
                        isModal={isModal}
                        showTabs={showTabs}
                        tabType={tabType}
                    />
                ) : null}
            </div>
        </TreeView>
    );
};

// Creates a memoized selector to check if any child is being renamed.
const makeGetHasRenamingChild = () =>
    //@ts-ignore
    createSelector(
        (state: IAppState, props: Props) => {
            let fields: IFieldsStore;
            if (props.TreeType == FieldVisibilityTypes.EXPORT) {
                fields = state.layoutfields;
            } else if (props.TreeType == FieldVisibilityTypes.REPORT) {
                fields = state.reportFields;
            } else {
                fields = state.fields;
            }
            return fields?.byFolder;
        },
        (state: IAppState, props: Props) => {
            let fields: IFieldsStore;
            if (props.TreeType == FieldVisibilityTypes.EXPORT) {
                fields = state.layoutfields;
            } else if (props.TreeType == FieldVisibilityTypes.REPORT) {
                fields = state.reportFields;
            } else {
                fields = state.fields;
            }

            // Use searchResultsByParent when available
            const hasSearchResults = state.vars.searchResults && Object.keys(state.vars.searchResults).length > 0;
            const byParent =
                hasSearchResults && state.vars.searchResultsByParent
                    ? state.vars.searchResultsByParent
                    : fields?.byParent;

            if (!byParent || !Object.prototype.hasOwnProperty.call(byParent, props.fieldKey)) {
                return null;
            }
            return byParent[props.fieldKey];
        },
        state => state.vars.renamingFieldFolderId,
        (fieldsById: { [workId: number]: IField }, childIds: Array<string | number>, renamingFieldFolderId: number) => {
            if (childIds == null || renamingFieldFolderId == null) {
                return false;
            }

            childIds = sortChildIds(childIds, fieldsById);

            return Boolean(childIds.find(x => fieldsById[x]?.workid == renamingFieldFolderId));
        }
    );

// Creates a memoized selector to check if this folder is being renamed and has no children.
const makeGetRenamingAndHasNoChildren = () =>
    createSelector(
        (state: IAppState, props: Props) =>
            props.TreeType == FieldVisibilityTypes.EXPORT
                ? state.layoutfields
                : props.TreeType == FieldVisibilityTypes.REPORT
                  ? state.reportFields
                  : state.fields,
        (_, props) => props.fieldKey,
        state => state.vars.renamingFieldFolderId,
        (fields, fieldKey, renamingFieldFolderId): boolean => {
            const folder = fields?.byFolder?.[fieldKey];
            if (!folder) return false;
            // Don't bother computing if not renaming - optimization to avoid recursive search on lots of updates
            if (renamingFieldFolderId != folder.workid) {
                return false;
            }
            return Boolean(
                folder.workid && fields?.byParent?.[folder.workid] && fields.byParent[folder.workid].length > 0
            );
        }
    );

// Creates a memoized selector to check if folder contains fields matching search string.
const makeGetFolderContainsSearchString = () =>
    createSelector(
        (state: IAppState, props: Props) => {
            // When search results exist, server already filtered, so folder always "contains" the search
            const hasSearchResults = state.vars.booleanSearchResultsReceived === true;
            if (hasSearchResults) {
                return null; // Signal that server filtering was used
            }
            return props.TreeType == FieldVisibilityTypes.EXPORT
                ? state.layoutfields
                : props.TreeType == FieldVisibilityTypes.REPORT
                  ? state.reportFields
                  : state.fields;
        },
        (_, props: Props) => props.fieldKey,
        (state: IAppState, props: Props): string | Array<string> =>
            props.isModal ? getModalTreeSearch(state) : getTreeSearch(state),
        (state: IAppState) => state.vars.searchResultsByParent,
        (fields, fieldKey, treeSearch, searchResultsByParent) => {
            // If server filtering was used (fields is null), check if this folder has children in search results
            if (fields === null) {
                return (
                    searchResultsByParent &&
                    searchResultsByParent[fieldKey] &&
                    searchResultsByParent[fieldKey].length > 0
                );
            }
            return fieldContainsSearchString(fields, fieldKey, treeSearch, true);
        }
    );

export default LazyFieldListFolder;
